import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../components/AuthContext';
import Logo from '../components/Logo';

/**
 * Catch-all for unknown routes (the last <Route path="*"> in App.js), so a
 * mistyped or stale link shows a translated message instead of a blank page.
 * Signed-in users go back to the dashboard, everyone else to the home page.
 */
function NotFoundPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { pathname } = useLocation();
  const target = token ? '/dashboard' : '/';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 px-4">
      <Helmet>
        <title>{t('notFound.title')}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
        <div className="flex justify-center mb-6">
          <Logo width={160} height={48} />
        </div>
        <p className="text-6xl font-bold text-primary-600 mb-2" aria-hidden="true">404</p>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3" data-testid="not-found-title">
          {t('notFound.title')}
        </h1>
        <p className="text-gray-700 dark:text-gray-300 mb-2">{t('notFound.body')}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-6 break-all">
          {/* LRI/PDI isolate keeps the URL left-to-right inside RTL text */}
          {t('notFound.requestedPath', { path: `\u2066${pathname}\u2069` })}
        </p>
        <Link
          to={target}
          className="inline-block px-5 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-medium"
          data-testid="not-found-back"
        >
          {token ? t('notFound.backToDashboard') : t('notFound.backToHome')}
        </Link>
      </div>
    </div>
  );
}

export default NotFoundPage;
